import * as Yup from 'yup';
import { useState } from 'react';
// next
import NextLink from 'next/link';
// form
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
// @mui
import { Alert, IconButton, InputAdornment, Typography, Stack, useTheme } from '@mui/material';
import { LoadingButton } from '@mui/lab';
// routes
import { PATH_AUTH } from '../../../routes/paths';
// hooks
import useAuth from '@common/hooks/useAuth';
import useIsMountedRef from '@common/hooks/useIsMountedRef';
// components
import Iconify from '@common/components/Iconify';
import { FormProvider, RHFCheckbox, RHFTextField } from '@common/components/hook-form';
import { mixpanelService } from '@common/analytics/mixpanel';
import { amplitudeService, getCampusTypeByDomain } from 'common/analytics/amplitude';
import useSiteConfig from 'common/hooks/useSiteConfig';
// ----------------------------------------------------------------------

type FormValuesProps = {
  email: string;
  password: string;
  remember: boolean;
  afterSubmit?: string;
};

export default function LoginForm() {
  const { login } = useAuth();
  const theme = useTheme();

  const isMountedRef = useIsMountedRef();
  const siteConfig = useSiteConfig();

  const [showPassword, setShowPassword] = useState(false);

  const LoginSchema = Yup.object().shape({
    email: Yup.string().email('Email must be a valid email address').required('Email is required'),
    password: Yup.string().required('Password is required'),
  });

  const defaultValues = {
    email: '',
    password: '',
    remember: true,
  };

  const methods = useForm<FormValuesProps>({
    resolver: yupResolver(LoginSchema),
    defaultValues,
  });

  const {
    setError,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = methods;

  const onSubmit = async (data: FormValuesProps) => {
    try {
      if (siteConfig.data?.domain != null)
        amplitudeService.trackClickLogin(getCampusTypeByDomain(siteConfig.data?.domain), 'email');

      await login(data.email.toLowerCase(), data.password);
      mixpanelService.track('Login Click', { using: 'App via Username/password' });
    } catch (error) {
      console.error(error);

      if (isMountedRef.current) {
        setError('afterSubmit', { ...error, message: error.message });
      }
    }
  };

  return (
    <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
      <Stack spacing={3}>
        {!!errors.afterSubmit && <Alert severity="error">{errors.afterSubmit.message}</Alert>}
        <RHFTextField name="email" label="Email address" InputLabelProps={{ shrink: true }} />
        <RHFTextField
          name="password"
          label="Password"
          type={showPassword ? 'text' : 'password'}
          InputLabelProps={{ shrink: true }} // AI-GEN - Cursor - GPT4
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                  <Iconify icon={showPassword ? 'eva:eye-fill' : 'eva:eye-off-fill'} />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
      </Stack>

      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ my: 2 }}>
        <RHFCheckbox name="remember" label="Remember me" />
        <NextLink
          href={PATH_AUTH.resetPassword}
          passHref
          style={{ textDecorationColor: theme.palette.primary.main }}
        >
          <Typography variant="subtitle2" sx={{ color: theme.palette.primary.main }}>
            Forgot password?
          </Typography>
        </NextLink>
      </Stack>

      <LoadingButton
        fullWidth
        size="large"
        type="submit"
        variant="contained"
        loading={isSubmitting}
      >
        Login
      </LoadingButton>
    </FormProvider>
  );
}
